import { CalendarArrowUp, CheckCircle2, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDateOnly } from '@/lib/utils'

interface ShipmentDateFields {
  estimated_ship_date?: string | null
  estimated_delivery_date?: string | null
  actual_ship_date?: string | null
}

function ShipmentDatePill({
  label,
  date,
  tone,
  icon,
  className,
  strike,
}: {
  label: string
  date: string
  tone: 'esd' | 'edd' | 'asd'
  icon: React.ReactNode
  className?: string
  strike?: boolean
}) {
  const tones = {
    esd: 'border-green-200 bg-green-50 text-green-700',
    edd: 'border-blue-200 bg-blue-50 text-blue-700',
    asd: 'border-green-700 bg-green-700 text-white',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
      title={`${label} ${formatDateOnly(date)}`}
    >
      {icon}
      <span className="font-semibold tracking-wide">{label}</span>
      <span className={cn('tabular-nums', strike && 'line-through opacity-70')}>
        {formatDateOnly(date)}
      </span>
    </span>
  )
}

/**
 * Inline pill-style badges for ESD / ASD / EDD. Once ASD is set, ESD is
 * shown struck through since it no longer matters.
 */
export function ShipmentDateBadges({
  shipment,
  className,
}: {
  shipment: ShipmentDateFields
  className?: string
}) {
  const { estimated_ship_date, estimated_delivery_date, actual_ship_date } = shipment
  if (!estimated_ship_date && !estimated_delivery_date && !actual_ship_date) return null

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {actual_ship_date && (
        <ShipmentDatePill
          label="ASD"
          date={actual_ship_date}
          tone="asd"
          icon={<CheckCircle2 className="h-3 w-3" />}
        />
      )}
      {estimated_ship_date && (
        <ShipmentDatePill
          label="ESD"
          date={estimated_ship_date}
          tone="esd"
          icon={<CalendarArrowUp className="h-3 w-3" />}
          strike={Boolean(actual_ship_date)}
        />
      )}
      {estimated_delivery_date && (
        <ShipmentDatePill
          label="EDD"
          date={estimated_delivery_date}
          tone="edd"
          icon={<MapPin className="h-3 w-3" />}
        />
      )}
    </div>
  )
}

/**
 * Full-size stacked cards for detail views. Shows each date with label + value.
 */
export function ShipmentDateCard({
  label,
  date,
  tone,
  icon,
  strike,
}: {
  label: string
  date: string | null | undefined
  tone: 'esd' | 'edd' | 'asd'
  icon: React.ReactNode
  strike?: boolean
}) {
  if (!date) return null
  const tones = {
    esd: {
      wrap: 'border-green-200 bg-green-50',
      icon: 'text-green-700',
      label: 'text-green-700',
      value: 'text-green-800',
    },
    edd: {
      wrap: 'border-blue-200 bg-blue-50',
      icon: 'text-blue-700',
      label: 'text-blue-700',
      value: 'text-blue-800',
    },
    asd: {
      wrap: 'border-green-700 bg-green-700',
      icon: 'text-white',
      label: 'text-white/90',
      value: 'text-white',
    },
  }
  const t = tones[tone]
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 ring-1 ring-inset ring-transparent',
        t.wrap,
      )}
    >
      <span className={cn('mt-0.5', t.icon)}>{icon}</span>
      <div>
        <p
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wider',
            t.label,
          )}
        >
          {label}
        </p>
        <p
          className={cn(
            'text-base font-bold tabular-nums',
            t.value,
            strike && 'line-through opacity-70',
          )}
        >
          {formatDateOnly(date)}
        </p>
      </div>
    </div>
  )
}
